import { useCallback, useState } from 'react';

import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { useCurrentAuthenticatedUser } from 'src/app/shared/hooks/session';
import type { MutationState } from 'src/app/shared/utils/async';

import type { ClosePetitionErrorCodes } from '../constants';
import { CLOSE_PETITION_ERROR_MAP, closePetitionErrors } from '../constants';

import { closePetition } from './api';

type ClosePetitionData = { status: 'ok' };
type RequestState = MutationState<{ data: ClosePetitionData }, { error: ClosePetitionErrorCodes }>;

type Callbacks = { onSuccess?: (data: ClosePetitionData) => void; onError?: (error: ClosePetitionErrorCodes) => void };
type HookOptions = Callbacks;
type MutationOptions = Callbacks;
type UseClosePetitionReturn = RequestState & {
	mutate: (options?: MutationOptions) => Promise<Extract<RequestState, { status: 'loaded' } | { status: 'error' }>>;
	reset: () => void;
};

export function useClosePetition(id: string, { onSuccess, onError }: HookOptions = {}): UseClosePetitionReturn {
	const utilityContext = useUtilityContext();
	const user = useCurrentAuthenticatedUser();
	const [state, setState] = useState<RequestState>({ status: 'idle' });

	const handleClosePetition = useCallback(async () => {
		setState({ status: 'loading' });

		try {
			const result = await closePetition({ id, user }, utilityContext);
			const successStatus = { status: 'loaded' as const, data: result };
			setState(successStatus);
			onSuccess?.(result);
			return successStatus;
		} catch (e) {
			const errorMessage = parseError(e);
			const errorStatus = { status: 'error' as const, error: errorMessage };
			setState(errorStatus);
			onError?.(errorMessage);
			return errorStatus;
		}
	}, [id, onError, onSuccess, user, utilityContext]);

	const handleReset = useCallback(() => {
		setState({ status: 'idle' });
	}, []);

	return { mutate: handleClosePetition, reset: handleReset, ...state };
}

function parseError(error: unknown) {
	const err = error instanceof Error ? error.message : undefined;
	return closePetitionErrors.find((val) => val === err) ?? CLOSE_PETITION_ERROR_MAP.UNKNOWN_ERROR;
}
